import type { ExistingFeeForDuplicate, FinePreviewRow } from './types.ts'

export function normalizeFeeDescription(value: string | null | undefined): string {
  return (value ?? '').trim().replace(/\s+/g, ' ').toLocaleLowerCase()
}

function duplicateKey(userId: string, description: string): string {
  return `${userId}\u0000${normalizeFeeDescription(description)}`
}

export function applyDuplicateWarnings(
  rows: readonly FinePreviewRow[],
  existingFees: readonly ExistingFeeForDuplicate[],
): FinePreviewRow[] {
  const existingKeys = new Set(
    existingFees
      .filter((fee) => normalizeFeeDescription(fee.description))
      .map((fee) => duplicateKey(fee.userId, fee.description ?? '')),
  )
  const previewKeys = new Set<string>()

  return rows.map((row) => {
    const key =
      row.matchedUserId && normalizeFeeDescription(row.description)
        ? duplicateKey(row.matchedUserId, row.description)
        : null
    const duplicateSource = key
      ? existingKeys.has(key)
        ? 'existing'
        : previewKeys.has(key)
          ? 'batch'
          : null
      : null
    const hasDuplicate = duplicateSource != null

    let skipRow = row.skipRow
    let skipReason = row.skipReason
    if (hasDuplicate && !row.hasDuplicate && skipReason !== 'manual') {
      skipRow = true
      skipReason = 'duplicate'
    } else if (!hasDuplicate && skipReason === 'duplicate') {
      skipRow = false
      skipReason = null
    }

    const next = {
      ...row,
      hasDuplicate,
      duplicateWarning: hasDuplicate
        ? duplicateSource === 'batch'
          ? `${row.matchedUserName ?? 'Member'} has the same fee earlier in this import`
          : `${row.matchedUserName ?? 'Member'} already has this fee on this date`
        : null,
      skipRow,
      skipReason,
      allowDuplicate: hasDuplicate && !skipRow,
    } satisfies FinePreviewRow

    if (key && !next.skipRow) previewKeys.add(key)
    return next
  })
}
